import { useEffect, useRef } from 'react';

const useSwipeToClose = (onClose, isOpen = true) => {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;

      const touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;
      
      const deltaX = touchCurrentX - touchStartX.current;
      const deltaY = touchCurrentY - touchStartY.current;
      
      // Only prevent default for downward swipes at the top of the modal
      // This allows normal scrolling but enables swipe-to-dismiss from the top
      const element = elementRef.current;
      if (element && deltaY > 30 && Math.abs(deltaY) > Math.abs(deltaX)) {
        const scrollTop = element.scrollTop;
        // Only prevent default if we're at the top of the modal content
        if (scrollTop <= 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Check if it's a vertical swipe (more vertical than horizontal)
      const isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);
      
      // Minimum swipe distance for dismissal
      const minSwipeDistance = 100;
      
      // Check for downward swipe to close modal (pull-to-dismiss pattern)
      if (isVerticalSwipe && deltaY > minSwipeDistance) {
        const element = elementRef.current;
        if (element) {
          const scrollTop = element.scrollTop;
          // Only close if we're at the top of the modal (not in the middle of scrolling)
          if (scrollTop <= 10) {
            onClose();
          }
        }
      }

      // Reset touch positions
      touchStartX.current = null;
      touchStartY.current = null;
    };

    const element = elementRef.current;
    if (element) {
      element.addEventListener('touchstart', handleTouchStart, { passive: false });
      element.addEventListener('touchmove', handleTouchMove, { passive: false });
      element.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [onClose, isOpen]);

  return elementRef;
};

export default useSwipeToClose;